import type {AttributeValueType} from './attribute'
import type {Instant, LocalizedMessage, UUID} from './common'

/* eslint-disable i18next/no-literal-string -- type definitions */
export const ValueSourceType = {
    LIST: 'LIST',
    ENUM: 'ENUM',
    SQL: 'SQL',
    SQL_LABELED: 'SQL_LABELED',
    CUSTOM_LIST: 'CUSTOM_LIST',
} as const
export type ValueSourceType = typeof ValueSourceType[keyof typeof ValueSourceType]
/* eslint-enable i18next/no-literal-string */

export interface AllowedValue {
    code: string
    label: string
}

export interface ListEntry {
    code: string
    label: string
}

export interface ValueSource {
    type: ValueSourceType
    values?: ListEntry[]
    typeName?: string
    sql?: string
    listName?: string
}

export interface ValidationRules {
    minLength?: number
    maxLength?: number
    pattern?: string
    min?: number
    max?: number
    maxOccurrences?: number
    valueSource?: ValueSource
    unique?: boolean
    derivedFromExpression?: string
}

export interface AttributeDefinition {
    id: UUID
    name: string
    displayName: string
    valueType: AttributeValueType
    authorityId: UUID | null
    authorityName: string | null
    operations: string[]
    validationRules: ValidationRules | null
    resolvedAllowedValues: AllowedValue[] | null
    required: boolean
    sortOrder: number
    active: boolean
    searchable: boolean
    filterable: boolean
    version: number
    createdAt: Instant
    updatedAt: Instant
    typeCodes: string[]
}

export interface AffectedEntity {
    entityId: UUID
    displayName: string
    currentValue: string
    entityType: string
    typeCode: string | null
}

export interface DefinitionChangeImpactResponse {
    affectedValueCount: number
    affectedEntityCount: number
    breakingChanges: LocalizedMessage[]
    affectedEntities: AffectedEntity[]
    migrationRequired: boolean
    totalValues: number | null
    checked: number
    done: boolean
    migrationSqlTemplate: string | null
}

export interface MigrationJobAcceptedResponse {
    message: string
    jobId: UUID
}


export interface BulkSetError {
    entityId: UUID
    errorMessage: string
}

export interface BulkSetAttribute {
    created: number
    updated: number
    unchanged: number
    skipped: number
    errors: number
    errorDetails: BulkSetError[]
}

export interface AttributeDefinitionTranslationEntry {
    label: string
    description: string
}

export interface AttributeDefinitionTranslation {
    translations: Record<string, AttributeDefinitionTranslationEntry>
    configuredLocales: string[]
    defaultLocale: string
}

export interface AttributeDefinitionReorderRequest {
    items: AttributeDefinitionReorderItem[]
}

export interface AttributeDefinitionReorderItem {
    id: UUID
    sortOrder: number
    version: number
}

export interface BulkSetAttributeRequest {
    definitionId: UUID
    entityIds?: UUID[]
    targetAllWithoutValue: boolean
    valueType?: AttributeValueType
    valueString?: string
    valueNumber?: number
    valueBoolean?: boolean
    valueDate?: string
    valueTimestamp?: Instant
    valueJson?: unknown
    values?: unknown[]
    valueTemplate?: string
}

export interface CreateAttributeDefinitionRequest {
    name: string
    displayName: string
    valueType: string
    authorityId?: UUID
    operations: string[]
    validationRules?: ValidationRules
    required: boolean
    sortOrder: number
    active: boolean
    searchable?: boolean | null
    filterable?: boolean | null
    typeCodes: string[]
    confirmed?: boolean
    migrationSql?: string
}

export interface DeleteAttributeDefinitionRequest {
    confirmed?: boolean
}

export interface SaveAttributeDefinitionTranslationsRequest {
    translations: Record<string, SaveAttributeDefinitionTranslationEntry>
}

export interface SaveAttributeDefinitionTranslationEntry {
    label: string
    description: string
}

export interface SetAttributeRequest {
    attributeName: string
    valueType?: AttributeValueType
    valueString?: string
    valueNumber?: number
    valueBoolean?: boolean
    valueDate?: string
    valueTimestamp?: Instant
    valueJson?: unknown
    values?: unknown[]
}

export interface BulkUpdateDefinitionItem {
    id: UUID
    name: string
    displayName: string
    valueType: AttributeValueType
    authorityId: UUID | null
    operations: string[]
    validationRules: ValidationRules | null
    required: boolean
    sortOrder: number
    active: boolean
    searchable: boolean
    filterable: boolean
    version: number
}

export interface UpdateAttributeDefinitionRequest {
    name: string
    displayName: string
    valueType: string
    authorityId?: UUID
    operations: string[]
    validationRules?: ValidationRules
    required: boolean
    sortOrder: number
    active: boolean
    searchable?: boolean | null
    filterable?: boolean | null
    version: number
    typeCodes: string[]
    confirmed?: boolean
    migrationSql?: string
}

export interface ValidateDefinitionRulesRequest {
    name: string
    displayName: string
    valueType: string
    validationRules?: ValidationRules
    required: boolean
    active: boolean
    sortOrder: number
    typeCodes: string[]
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const AttributeDefinitionResponseFields = {
    id: 'id',
    name: 'name',
    displayName: 'displayName',
    valueType: 'valueType',
    authorityId: 'authorityId',
    authorityName: 'authorityName',
    operations: 'operations',
    validationRules: 'validationRules',
    resolvedAllowedValues: 'resolvedAllowedValues',
    required: 'required',
    sortOrder: 'sortOrder',
    active: 'active',
    searchable: 'searchable',
    filterable: 'filterable',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
    typeCodes: 'typeCodes',
} as const

export const AttributeDefinitionTranslationResponseFields = {
    translations: 'translations',
    configuredLocales: 'configuredLocales',
    defaultLocale: 'defaultLocale',
} as const

export const AttributeResponseFields = {
    id: 'id',
    definitionId: 'definitionId',
    name: 'name',
    displayName: 'displayName',
    valueType: 'valueType',
    value: 'value',
    values: 'values',
    authorityId: 'authorityId',
    authorityName: 'authorityName',
    allowedOperations: 'allowedOperations',
    version: 'version',
    observedAt: 'observedAt',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const BulkSetAttributeRequestFields = {
    definitionId: 'definitionId',
    entityIds: 'entityIds',
    targetAllWithoutValue: 'targetAllWithoutValue',
    valueType: 'valueType',
    valueString: 'valueString',
    valueNumber: 'valueNumber',
    valueBoolean: 'valueBoolean',
    valueDate: 'valueDate',
    valueTimestamp: 'valueTimestamp',
    valueJson: 'valueJson',
    values: 'values',
    valueTemplate: 'valueTemplate',
} as const

export const BulkSetAttributeResponseFields = {
    created: 'created',
    updated: 'updated',
    unchanged: 'unchanged',
    skipped: 'skipped',
    errors: 'errors',
    errorDetails: 'errorDetails',
} as const

export const CreateAttributeDefinitionRequestFields = {
    name: 'name',
    displayName: 'displayName',
    valueType: 'valueType',
    authorityId: 'authorityId',
    operations: 'operations',
    validationRules: 'validationRules',
    required: 'required',
    sortOrder: 'sortOrder',
    active: 'active',
    searchable: 'searchable',
    filterable: 'filterable',
    typeCodes: 'typeCodes',
    confirmed: 'confirmed',
    migrationSql: 'migrationSql',
} as const

export const MigrationJobAcceptedResponseFields = {
    message: 'message',
    jobId: 'jobId',
} as const

export const SaveAttributeDefinitionTranslationsRequestFields = {
    translations: 'translations',
} as const

export const SetAttributeRequestFields = {
    attributeName: 'attributeName',
    valueType: 'valueType',
    valueString: 'valueString',
    valueNumber: 'valueNumber',
    valueBoolean: 'valueBoolean',
    valueDate: 'valueDate',
    valueTimestamp: 'valueTimestamp',
    valueJson: 'valueJson',
    values: 'values',
} as const

export const UpdateAttributeDefinitionRequestFields = {
    name: 'name',
    displayName: 'displayName',
    valueType: 'valueType',
    authorityId: 'authorityId',
    operations: 'operations',
    validationRules: 'validationRules',
    required: 'required',
    sortOrder: 'sortOrder',
    active: 'active',
    searchable: 'searchable',
    filterable: 'filterable',
    version: 'version',
    typeCodes: 'typeCodes',
    confirmed: 'confirmed',
    migrationSql: 'migrationSql',
} as const

export const ValidateDefinitionChangeRequestFields = {
    displayName: 'displayName',
    valueType: 'valueType',
    validationRules: 'validationRules',
    required: 'required',
    active: 'active',
    typeCodes: 'typeCodes',
} as const

export const ValidateDefinitionRulesRequestFields = {
    name: 'name',
    displayName: 'displayName',
    valueType: 'valueType',
    validationRules: 'validationRules',
    required: 'required',
    active: 'active',
    sortOrder: 'sortOrder',
    typeCodes: 'typeCodes',
} as const

export const AttributeDefinitionFilterParams = {
    active: 'active',
} as const

export const AttributeDefinitionTabKeys = {
    identities: 'identities',
    parties: 'parties',
    resources: 'resources',
    functionGroups: 'functionGroups',
    targetSystems: 'targetSystems',
    connectors: 'connectors',
    reports: 'reports',
} as const

export interface BulkImportFieldError {
    definitionName: string | null
    errorCode: string
    errorMessage: string
}

export interface BulkImportRowResult {
    rowIndex: number
    identifier: string | null
    status: string
    errors: BulkImportFieldError[]
}

export interface BulkImportActionResult {
    results: BulkImportRowResult[]
}

export interface BulkImport {
    create: BulkImportActionResult | null
    update: BulkImportActionResult | null
    delete: BulkImportActionResult | null
}

export interface BulkImportRequest {
    create?: Record<string, unknown>[]
    update?: Record<string, unknown>[]
    delete?: Record<string, unknown>[]
}

export const BulkImportResponseFields = {
    create: 'create',
    update: 'update',
    delete: 'delete',
    results: 'results',
    rowIndex: 'rowIndex',
    identifier: 'identifier',
    status: 'status',
    errors: 'errors',
    definitionName: 'definitionName',
    errorCode: 'errorCode',
    errorMessage: 'errorMessage',
} as const

export const BulkImportRequestFields = {
    create: 'create',
    update: 'update',
    delete: 'delete',
} as const
/* eslint-enable i18next/no-literal-string */
